/**
 * Extracts variable names wrapped in double curly braces from a template string.
 *
 * Example:
 *   TExtractTemplateKeys<"Hello {{name}} and {{id}}"> === "name" | "id"
 */
type TExtractTemplateKeys<T extends string> = T extends `${string}{{${infer Key}}}${infer Rest}`
  ? Key | TExtractTemplateKeys<Rest>
  : never;

export const URLUtils = {
  /**
   * Replaces placeholders in a template URL with actual values.
   *
   * Example:
   * ```ts
   * URLUtils.fromTemplate(
   *   "https://example.com?name={{user}}&age={{age}}",
   *   { user: "John", age: 30 }
   * );
   * // => "https://example.com?name=John&age=30"
   * ```
   *
   * @template T The template string literal type.
   * @param {T} template - The URL template containing placeholders like `{{key}}`.
   * @param {Record<TExtractTemplateKeys<T>, string | number | boolean | Date>} parameters - An object with values to replace placeholders.
   * @returns {string} The resulting URL with all placeholders replaced by encoded values.
   */
  fromTemplate: <T extends string>(
    template: T,
    parameters: Record<TExtractTemplateKeys<T>, string | number | boolean | Date>,
  ): string => {
    return template.replace(/{{(.*?)}}/g, (_, rawKey: string) => {
      const key = rawKey.trim() as TExtractTemplateKeys<T>;
      const value = parameters[key];

      return value !== undefined ? encodeURIComponent(String(value)) : '';
    });
  },
};
